import { useMemo } from "react";
import type { ComplianceSummary, RepeatOffender } from "../types";
import { windowLong } from "../lib/reportingWindow";

interface Props {
  summary: ComplianceSummary;
}

function isContractor(g: string): boolean {
  return g === "contractor";
}

export default function RepeatOffenders({ summary }: Props) {
  const offenders = summary.repeat_offenders ?? [];
  const repeat = useMemo(
    () => offenders.filter((o) => o.miss_count >= 2),
    [offenders],
  );
  const employees = repeat.filter((o) => !isContractor(o.user_group.toLowerCase()));
  const contractors = repeat.filter((o) => isContractor(o.user_group.toLowerCase()));

  const wl = windowLong(summary);

  if (repeat.length === 0) {
    return (
      <section className="rounded-2xl bg-white shadow-card border border-slate-200/70">
        <header className="px-5 py-4">
          <h2 className="text-base font-bold tracking-tight text-rancho-ink">
            Repeat month-end offenders
          </h2>
          <p className="text-xs text-rancho-slate mt-0.5">
            Nobody missed the month-end deadline more than once {wl}.
          </p>
        </header>
      </section>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <OffendersTable
        title="Employees"
        offenders={employees}
        emptyMsg={`No employee repeat offenders ${wl}.`}
        windowLabel={wl}
      />
      <OffendersTable
        title="Contractors"
        offenders={contractors}
        emptyMsg={`No contractor repeat offenders ${wl}.`}
        windowLabel={wl}
      />
    </div>
  );
}

function OffendersTable({
  title,
  offenders,
  emptyMsg,
  windowLabel,
}: {
  title: string;
  offenders: RepeatOffender[];
  emptyMsg: string;
  windowLabel: string;
}) {
  const top = offenders.slice(0, 12);
  const empty = offenders.length === 0;

  return (
    <section className="rounded-2xl bg-white shadow-card border border-slate-200/70 overflow-hidden">
      <header className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-red-50 to-transparent">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-bold tracking-tight text-rancho-ink">
              Repeat month-end offenders {title}
            </h2>
            <p className="text-xs text-rancho-slate mt-0.5">
              {empty
                ? emptyMsg
                : `${offenders.length} ${title.toLowerCase()} missed 2+ month-end deadlines ${windowLabel}. Showing top ${top.length}.`}
            </p>
          </div>
          {!empty && (
            <span className="inline-flex items-center gap-2 rounded-full bg-red-100 text-red-700 px-3 py-1 text-xs font-semibold">
              <span className="w-2 h-2 rounded-full bg-red-700" />
              Action needed
            </span>
          )}
        </div>
      </header>
      {!empty && (
        <div className="overflow-x-auto scroll-thin">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-rancho-slate">
                <th className="px-5 py-3">Person</th>
                <th className="px-3 py-3">Team</th>
                <th className="px-3 py-3 text-right">Misses</th>
                <th className="px-3 py-3">Months affected</th>
              </tr>
            </thead>
            <tbody>
              {top.map((o) => (
                <tr
                  key={o.person_id}
                  className="border-t border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-5 py-3 font-semibold text-rancho-ink">{o.name}</td>
                  <td className="px-3 py-3 text-rancho-slate">{o.team}</td>
                  <td className="px-3 py-3 text-right">
                    <span className="inline-flex items-center justify-center min-w-[32px] h-6 px-2 rounded-full bg-red-700 text-white text-xs font-bold">
                      {o.miss_count}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-rancho-slate">
                    {o.miss_months.join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
